import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { api } from '../api/client';
import type { SkuSugerido } from '../components/BuscadorSku';

/**
 * What a screen shows for a stored SKU (the article's internal id): its code and identity supplier. Lines and forms keep
 * storing `skuInterno`; the display comes from this app-wide cache (GET catalogo/identidad, batched).
 */
export interface IdentidadArticulo {
  skuInterno: string;
  codigo: string;
  proveedor: { id: number; nombre: string } | null;
  /** Another article has the same code: listings then also show the supplier. */
  repetido: boolean;
}

/** «A y B», «A, B y C»; «e» before a name that starts with an /i/ sound (ETERNA e IBICO, but «y Hierro»). */
export function listaDeProveedores(nombres: string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? '';
  const ultimo = nombres[nombres.length - 1];
  const conjuncion = /^h?[ií](?![aeiouáéíóú])/i.test(ultimo.trim()) ? 'e' : 'y';
  return `${nombres.slice(0, -1).join(', ')} ${conjuncion} ${ultimo}`;
}

const URL_IDENTIDAD = '/api/v3/catalogo/identidad';
/** After a failed lookup a SKU is asked again only after this pause (a 429 must not loop). */
const REINTENTO_MS = 5000;
/** The backend's MAX_SKUS_IDENTIDAD (catalogo.service.ts). */
const POR_CONSULTA = 200;
const DEMORA_MS = 20;

const cache = new Map<string, IdentidadArticulo>();
/** SKUs already asked and answered (present or absent): never asked again in this page load. */
const consultados = new Set<string>();
/** Failed SKUs: when they may be asked again. */
const reintentarDesde = new Map<string, number>();
const enCurso = new Map<string, Promise<void>>();
let cola: string[] = [];
let ciclo: { promesa: Promise<void>; listo: () => void } | null = null;
let temporizador: ReturnType<typeof setTimeout> | null = null;
let version = 0;
const oyentes = new Set<() => void>();

function avisar() {
  version++;
  oyentes.forEach(o => o());
}

async function consultar(lote: string[]) {
  try {
    const r = await api.get<IdentidadArticulo[]>(`${URL_IDENTIDAD}?skus=${encodeURIComponent(lote.join(','))}`);
    const pedidos = new Set(lote);
    for (const i of Array.isArray(r) ? r : []) {
      if (i && typeof i.skuInterno === 'string' && typeof i.codigo === 'string' && pedidos.has(i.skuInterno)) cache.set(i.skuInterno, i);
    }
  } catch {
    // Failed: fields keep showing the raw SKU; a later render asks again after REINTENTO_MS.
    for (const s of lote) { consultados.delete(s); reintentarDesde.set(s, Date.now() + REINTENTO_MS); }
  }
  avisar();
}

async function vaciarCola() {
  const lote = cola;
  const actual = ciclo;
  cola = []; ciclo = null; temporizador = null;
  const partes: Promise<void>[] = [];
  for (let i = 0; i < lote.length; i += POR_CONSULTA) partes.push(consultar(lote.slice(i, i + POR_CONSULTA)));
  await Promise.all(partes);
  lote.forEach(s => enCurso.delete(s));
  actual?.listo();
}

/** Identity of each SKU (the ones the backend knows), batched with every other request of the next 20 ms. */
export async function cargarIdentidades(skus: string[]): Promise<Map<string, IdentidadArticulo>> {
  const unicos = [...new Set(skus.map(s => (s ?? '').trim()).filter(Boolean))];
  for (const s of unicos) {
    if (cache.has(s) || consultados.has(s) || (reintentarDesde.get(s) ?? 0) > Date.now()) continue;
    if (!ciclo) {
      let listo = () => {};
      const promesa = new Promise<void>(r => { listo = r; });
      ciclo = { promesa, listo };
    }
    consultados.add(s);
    cola.push(s);
    enCurso.set(s, ciclo.promesa);
  }
  if (cola.length > 0 && !temporizador) temporizador = setTimeout(() => { void vaciarCola(); }, DEMORA_MS);
  await Promise.all(unicos.map(s => enCurso.get(s)).filter(Boolean));
  return new Map(unicos.flatMap(s => (cache.has(s) ? [[s, cache.get(s)!] as const] : [])));
}

/** A picked article: the field shows its code with no lookup. */
export function recordarIdentidad(s: SkuSugerido): void {
  const previa = cache.get(s.skuInterno);
  cache.set(s.skuInterno, {
    skuInterno: s.skuInterno,
    codigo: s.codigo || s.skuInterno,
    proveedor: s.proveedor ?? null,
    repetido: previa?.repetido ?? false,
  });
  consultados.add(s.skuInterno);
  avisar();
}

const suscribir = (o: () => void) => { oyentes.add(o); return () => { oyentes.delete(o); }; };
const versionActual = () => version;

/** Loads the identities of these SKUs and re-renders when they arrive. */
export function useIdentidades(skus: Array<string | null | undefined>): (sku?: string | null) => IdentidadArticulo | undefined {
  const v = useSyncExternalStore(suscribir, versionActual);
  const firma = [...new Set(skus.map(s => (s ?? '').trim()).filter(Boolean))].join('\n');
  // Every render (not only on a new signature): a SKU whose lookup failed is retried once its pause is over. Known SKUs
  // cost only a set check.
  useEffect(() => {
    if (firma) void cargarIdentidades(firma.split('\n'));
  });
  return useCallback((sku?: string | null) => (sku ? cache.get(sku.trim()) : undefined), [v]);
}

/**
 * Listing rows plus their article's code and identity supplier as plain fields (`codigoArticulo`, `proveedorArticulo`),
 * so a DataTable column and its CSV/XLSX export carry them. One batched lookup for the whole listing.
 */
export function useFilasConIdentidad<T>(filas: T[], skuDe: (f: T) => string | null | undefined): Array<T & { codigoArticulo: string; proveedorArticulo: string }> {
  const identidad = useIdentidades(filas.map(skuDe));
  return filas.map(f => {
    const sku = skuDe(f) ?? '';
    const i = identidad(sku);
    return { ...f, codigoArticulo: textoArticulo(sku, i), proveedorArticulo: i?.proveedor?.nombre ?? '' };
  });
}

/** Text a field shows for a stored SKU: its code, or the SKU itself while loading or after a failure. */
export function textoArticulo(sku: string, identidad?: IdentidadArticulo): string {
  return identidad?.codigo ?? sku;
}

/** Plain-text form of CodigoArticulo: the code, plus « · {proveedor}» when another article has the same code. */
export function etiquetaArticulo(sku: string, identidad?: IdentidadArticulo): string {
  if (!identidad) return sku;
  return identidad.repetido ? `${identidad.codigo} · ${identidad.proveedor?.nombre ?? 'sin proveedor'}` : identidad.codigo;
}

export type Resolucion = { tipo: 'unico'; articulo: SkuSugerido } | { tipo: 'ambiguo'; mensaje: string } | { tipo: 'ninguno' };

const clave = (c?: string | null) => (c ?? '').trim().toLowerCase();

/** A typed code against an exact lookup's results; the supplier, when known, picks among several (rule 8). */
export function elegirPorCodigo(resultados: SkuSugerido[], codigo: string, proveedorId?: number | string | null): Resolucion {
  const buscado = clave(codigo);
  const exactos = (Array.isArray(resultados) ? resultados : [])
    .filter(r => clave(r.codigo || r.skuInterno) === buscado || clave(r.skuInterno) === buscado);
  if (exactos.length === 1) return { tipo: 'unico', articulo: exactos[0] };
  if (exactos.length === 0) return { tipo: 'ninguno' };
  if (proveedorId != null && proveedorId !== '') {
    const delProveedor = exactos.filter(r => r.proveedor && String(r.proveedor.id) === String(proveedorId));
    if (delProveedor.length === 1) return { tipo: 'unico', articulo: delProveedor[0] };
  }
  const nombres = [...new Set(exactos.map(r => r.proveedor?.nombre ?? 'sin proveedor'))];
  return { tipo: 'ambiguo', mensaje: `Este código existe en ${listaDeProveedores(nombres)}: elegí cuál` };
}

/**
 * Before saving: resolves each typed text that is not a known article (a click on «Guardar» before the blur lookup
 * answered). `sku` maps text → skuInterno for single matches; `ambiguos` holds the notices that must block the save;
 * `fallidos` the texts whose lookup failed (not the same as «not found»: the caller decides whether to block).
 */
export async function asegurarArticulos(
  textos: string[], resolver: (c: string) => Promise<SkuSugerido[]>, proveedorId?: number | string | null,
): Promise<{ sku: Map<string, string>; ambiguos: string[]; fallidos: string[] }> {
  const sku = new Map<string, string>();
  const ambiguos: string[] = [];
  const fallidos: string[] = [];
  const pendientes = [...new Set(textos.map(t => (t ?? '').trim()).filter(t => t && !cache.has(t)))];
  await Promise.all(pendientes.map(async t => {
    let r: Resolucion;
    try {
      r = elegirPorCodigo(await resolver(t), t, proveedorId);
    } catch {
      fallidos.push(t);
      return;
    }
    if (r.tipo === 'unico') {
      sku.set(t, r.articulo.skuInterno);
      recordarIdentidad(r.articulo);
    } else if (r.tipo === 'ambiguo') ambiguos.push(r.mensaje);
  }));
  return { sku, ambiguos, fallidos };
}

/** Tests only: forgets everything (vitest.setup.ts calls it after each test). */
export function limpiarIdentidades(): void {
  if (temporizador) clearTimeout(temporizador);
  ciclo?.listo();
  cache.clear(); consultados.clear(); enCurso.clear(); reintentarDesde.clear();
  cola = []; ciclo = null; temporizador = null;
  avisar();
}
